import { useMemo, useState, type ComponentType } from 'react'

import {
  AlertTriangle,
  ArrowLeft,
  Building2,
  Calendar,
  CalendarCheck,
  CheckCircle2,
  ChevronRight,
  CircleAlert,
  Clock,
  MapPin,
  MapPinOff,
  Pencil,
  Phone,
  type LucideIcon,
} from 'lucide-react'

import { cn } from '@/lib/utils'
import { ConfirmDialog } from '@/shared/components/ui'
import { initialsOf } from '@/shared/utils/initials'

import { SiteIssuesTab } from './tabs/SiteIssuesTab'
import { SiteSurveysTab } from './tabs/SiteSurveysTab'
import { useResetSiteFieldData, useSiteDetail } from '../hooks/useSiteDetail'

import type { SiteDetail, SiteIssueItem, SiteStats } from '../api/siteApi'

// Backend `stats.issues_active` / `issues_resolved` can drift from the loaded
// issues list (server-side aggregation lag, or under-counting). We always
// re-derive these tile values from the issues actually present in the
// response so the cards never disagree with the Issues tab below them.
// Lifecycle v2: active = anything not completed; resolved = completed.
function countIssuesByBucket(items: SiteIssueItem[]): { active: number; resolved: number } {
  let active = 0
  let resolved = 0
  for (const issue of items) {
    if (issue.status === 'completed') {
      resolved += 1
    } else {
      active += 1
    }
  }
  return { active, resolved }
}

// ─── Derived status (no API field for this on SiteInfo) ──────────────────

type DerivedStatus = 'completed' | 'in_progress' | 'missing_survey' | 'issues' | 'no_survey'

function deriveStatus(stats: SiteStats, lastSurveyDate?: string | null): DerivedStatus {
  if (stats.surveys_missed > 0 && !lastSurveyDate) {
    return 'missing_survey'
  }
  if (stats.issues_active > 0) {
    return 'issues'
  }
  if (stats.surveys_completed > 0) {
    return 'completed'
  }
  if (stats.surveys_scheduled > 0) {
    return 'in_progress'
  }
  return 'no_survey'
}

interface StatusTone {
  label: string
  cssVar: string
  softVar: string
  tintHex: string
  glyphClass: string
  pillClass: string
}

const STATUS_TONE: Record<DerivedStatus, StatusTone> = {
  completed: {
    label: 'Completed',
    cssVar: 'var(--color-success)',
    softVar: 'var(--color-success-soft)',
    tintHex: '#f0faf4',
    glyphClass: 'text-success',
    pillClass: 'text-success',
  },
  in_progress: {
    label: 'In Progress',
    cssVar: 'var(--color-brand)',
    softVar: 'var(--color-brand-soft)',
    tintHex: '#f1f5fe',
    glyphClass: 'text-brand',
    pillClass: 'text-brand',
  },
  missing_survey: {
    label: 'Missing Survey',
    cssVar: 'var(--color-warning)',
    softVar: 'var(--color-warning-soft)',
    tintHex: '#fef9ec',
    glyphClass: 'text-warning',
    pillClass: 'text-warning',
  },
  issues: {
    label: 'Issues Active',
    cssVar: 'var(--color-danger)',
    softVar: 'var(--color-danger-soft)',
    tintHex: '#fdf3f1',
    glyphClass: 'text-danger',
    pillClass: 'text-danger',
  },
  no_survey: {
    label: 'No Survey',
    cssVar: 'var(--color-muted-text)',
    softVar: 'var(--color-line-2)',
    tintHex: '#f7f9fc',
    glyphClass: 'text-muted-text',
    pillClass: 'text-muted-text',
  },
}

// ─── Hero stat tile ──────────────────────────────────────────────────────

interface HeroStatProps {
  icon: LucideIcon
  value: number
  label: string
  cssVar: string
  iconBg: string
  iconColor: string
  numColor: string
  isFirst?: boolean
}

function HeroStat({
  icon: Icon,
  value,
  label,
  iconBg,
  iconColor,
  numColor,
  isFirst,
}: HeroStatProps) {
  return (
    <div
      className={cn(
        'relative flex items-center gap-2.5 bg-white px-4 py-3.5',
        !isFirst && 'border-line border-l',
      )}
    >
      <div className={cn('grid size-[34px] place-items-center rounded-[9px]', iconBg, iconColor)}>
        <Icon size={15} strokeWidth={2} />
      </div>
      <div className="flex flex-col leading-[1.15]">
        <span
          className={cn(
            'text-[22px] leading-none font-extrabold tracking-[-0.02em] tabular-nums',
            numColor,
          )}
        >
          {String(value).padStart(2, '0')}
        </span>
        <span className="text-muted-text mt-[3px] text-[12px] font-medium">{label}</span>
      </div>
    </div>
  )
}

// ─── Page ─────────────────────────────────────────────────────────────────

/** Injected edit affordance. When provided, the "Edit info" button renders and
 *  this component is mounted with the loaded site when the button is clicked.
 *  Consumers that can't/shouldn't edit (e.g. the GIS map view) simply omit it. */
export type SiteEditModalComponent = ComponentType<{
  open: boolean
  onClose: () => void
  site: SiteDetail
}>

interface SiteDetailPageProps {
  siteId: string
  onBack: () => void
  /** Controlled active tab (e.g. from the URL). Falls back to local state when omitted. */
  tab?: 'surveys' | 'issues'
  onTabChange?: (tab: 'surveys' | 'issues') => void
  /** Optional edit modal; when omitted the page is view-only (no "Edit info"). */
  EditModalComponent?: SiteEditModalComponent
}

export function SiteDetailPage({
  siteId,
  onBack,
  tab,
  onTabChange,
  EditModalComponent,
}: SiteDetailPageProps) {
  // Tab is controlled by the consumer (URL-backed on the entities route) or,
  // when unmanaged (e.g. embedded in the GIS panel), by local state.
  const [localTab, setLocalTab] = useState<'surveys' | 'issues'>('surveys')
  const activeTab = tab ?? localTab
  const setActiveTab = onTabChange ?? setLocalTab
  const [showEdit, setShowEdit] = useState(false)
  const [showResetFieldData, setShowResetFieldData] = useState(false)
  const { data: site, isLoading } = useSiteDetail(siteId)
  const { mutate: resetFieldData, isPending: isResettingFieldData } = useResetSiteFieldData(
    siteId,
    site?.entity_id,
  )

  const status = useMemo<DerivedStatus | null>(() => {
    if (!site) {
      return null
    }
    return deriveStatus(site.stats, site.last_survey_date)
  }, [site])

  if (isLoading || !site) {
    return (
      <div className="font-display text-muted-text grid h-full place-items-center text-[13px] font-medium">
        Loading site details…
      </div>
    )
  }

  const siteName = site.site_name ?? site.name ?? ''
  const s = site.stats
  const progress = s.survey_progress_percent ?? 0
  const tone = status ? STATUS_TONE[status] : STATUS_TONE.no_survey

  const lastSurveyed = site.last_survey_date
    ? new Date(site.last_survey_date).toLocaleDateString('en-GB', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
      })
    : null

  const officer = site.assigned_officer
  const surveysBadge = site.surveys?.total_count ?? site.surveys?.items?.length ?? 0

  // Always derive issue counts from the loaded items rather than trusting
  // `stats.issues_active` / `issues_resolved` — those are server-side and
  // were observed returning 0 when the issues tab clearly has rows.
  const issueItems = site.issues?.items ?? []
  const { active: issuesActiveCount, resolved: issuesResolvedCount } = countIssuesByBucket(issueItems)
  const issuesBadge = site.issues?.total_count ?? issueItems.length

  return (
    <div className="font-display flex h-full flex-col gap-3 pt-1 pb-6">
      {/* ─── Static top: breadcrumb + hero card (stays put when content scrolls) ─── */}
      <div className="flex shrink-0 flex-col gap-3">
        {/* ── Breadcrumb ── */}
        <nav
          aria-label="Breadcrumb"
          className="text-muted-text inline-flex items-center gap-1.5 text-[12px]"
        >
          <button
            type="button"
            onClick={onBack}
            className="text-ink-3 hover:bg-line-2 hover:text-ink inline-flex items-center gap-1.5 rounded-md px-2 py-1 font-medium transition-colors"
          >
            <ArrowLeft size={12} strokeWidth={2.2} />
            <span className="truncate">{site.entity_name}</span>
          </button>
          <ChevronRight size={12} className="text-muted-text-2" />
          <span className="text-ink truncate font-semibold">{siteName}</span>
        </nav>

        {/* ─────────── HERO CARD ─────────── */}
        <section className="border-line overflow-hidden rounded-[14px] border bg-white shadow-[0_2px_6px_-2px_rgba(11,21,48,0.04)]">
          {/* Top section: image | info | actions */}
          <div className="grid grid-cols-1 items-center gap-[18px] p-[18px] lg:grid-cols-[160px_minmax(0,1fr)_auto]">
            {/* Square site image (status-tinted) */}
            <div
              className="relative isolate overflow-hidden rounded-[14px]"
              style={{
                width: '100%',
                aspectRatio: '1 / 1',
                maxWidth: '160px',
                background: `linear-gradient(155deg, ${tone.tintHex} 0%, ${tone.softVar} 50%, ${tone.softVar} 100%)`,
              }}
            >
              {/* Corner pill */}
              <span
                className={cn(
                  'absolute top-2.5 left-2.5 z-[2] inline-flex items-center gap-1 rounded-full px-2 py-[3px] text-[9.5px] font-bold tracking-[0.08em] uppercase shadow-[0_4px_10px_-4px_rgba(11,21,48,0.2)] backdrop-blur',
                  tone.pillClass,
                )}
                style={{ background: 'rgba(255,255,255,0.92)' }}
              >
                <span className="size-[5px] rounded-full bg-current" />
                {tone.label}
              </span>

              {/* Site image OR glyph fallback */}
              {site.site_image_url ? (
                <>
                  <img
                    src={site.site_image_url}
                    alt={siteName}
                    className="absolute inset-0 h-full w-full object-cover"
                    onError={(e) => {
                      e.currentTarget.style.display = 'none'
                    }}
                  />
                  <span
                    aria-hidden="true"
                    className="pointer-events-none absolute inset-0 opacity-[0.25]"
                    style={{
                      background: `radial-gradient(circle at 25% 18%, rgba(255,255,255,0.35), transparent 55%), radial-gradient(circle at 80% 90%, ${tone.cssVar} 0%, transparent 60%)`,
                    }}
                  />
                </>
              ) : (
                <>
                  <span
                    aria-hidden="true"
                    className="pointer-events-none absolute inset-0 opacity-[0.25]"
                    style={{
                      background: `radial-gradient(circle at 25% 18%, rgba(255,255,255,0.35), transparent 55%), radial-gradient(circle at 80% 90%, ${tone.cssVar} 0%, transparent 60%)`,
                    }}
                  />
                  <div
                    className={cn('relative z-[1] grid h-full w-full place-items-center', tone.glyphClass)}
                  >
                    <Building2 size={58} strokeWidth={1.4} />
                  </div>
                </>
              )}
            </div>

            {/* Site info column */}
            <div className="flex min-w-0 flex-col gap-2">
              <h1 className="text-ink truncate text-[20px] leading-[1.15] font-bold tracking-[-0.015em]">
                {siteName}
              </h1>
              <div className="text-ink-3 flex flex-wrap items-center gap-x-[18px] gap-y-1.5 text-[13px]">
                {site.address && (
                  <span className="inline-flex items-center gap-1.5">
                    <MapPin size={13} strokeWidth={2} className="text-muted-text shrink-0" />
                    <span className="truncate">{site.address}</span>
                  </span>
                )}
                {site.contact_number && (
                  <span className="inline-flex items-center gap-1.5">
                    <Phone size={13} strokeWidth={2} className="text-muted-text shrink-0" />
                    <strong className="text-ink-2 font-semibold tabular-nums">
                      {site.contact_number}
                    </strong>
                  </span>
                )}
                {lastSurveyed && (
                  <span className="inline-flex items-center gap-1.5">
                    <Calendar size={13} strokeWidth={2} className="text-muted-text shrink-0" />
                    Last surveyed{' '}
                    <strong className="text-ink-2 font-semibold tabular-nums">
                      {lastSurveyed}
                    </strong>
                  </span>
                )}
              </div>

              {/* Survey progress */}
              <div className="mt-1 flex items-center gap-2.5">
                <span className="text-muted-text text-[10.5px] font-bold tracking-[0.08em] uppercase">
                  Survey progress
                </span>
                <div className="bg-line-2 h-1.5 max-w-[240px] flex-1 overflow-hidden rounded-full">
                  <div
                    className="h-full rounded-full"
                    style={{
                      width: `${Math.max(0, Math.min(100, progress))}%`,
                      background: 'linear-gradient(90deg, var(--color-success) 0%, #5dd39e 100%)',
                    }}
                  />
                </div>
                <span className="text-ink text-[13px] font-bold tabular-nums">{progress}%</span>
              </div>
            </div>

            {/* Actions column */}
            <div className="flex flex-col items-end justify-center gap-2">
              {officer ? (
                <div className="bg-line-2 border-line inline-flex items-center gap-2.5 rounded-full border py-[7px] pr-3 pl-[7px]">
                  <span
                    className="grid size-7 shrink-0 place-items-center rounded-full text-[11px] font-bold text-white shadow-[0_4px_10px_-4px_rgba(11,59,145,0.4)]"
                    style={{ background: 'linear-gradient(155deg, #1450c8 0%, #0b3b91 100%)' }}
                  >
                    {initialsOf(officer.display_name)}
                  </span>
                  <div className="flex flex-col leading-[1.15]">
                    <span className="text-muted-text text-[9px] font-bold tracking-[0.08em] uppercase">
                      Field officer
                    </span>
                    <span className="text-ink text-[13px] font-bold">{officer.display_name}</span>
                  </div>
                </div>
              ) : (
                <div className="bg-warning-soft text-warning border-warning-soft inline-flex items-center gap-1.5 rounded-full border px-3 py-[7px] text-[12px] font-semibold">
                  <AlertTriangle size={12} strokeWidth={2.2} />
                  No officer assigned
                </div>
              )}
              {EditModalComponent && (
                <button
                  type="button"
                  onClick={() => setShowEdit(true)}
                  className="border-line text-ink-2 hover:border-brand hover:text-brand hover:bg-brand-tint inline-flex items-center gap-1.5 rounded-[8px] border bg-white px-3.5 py-[7px] text-[12px] font-semibold transition-colors"
                >
                  <Pencil size={11} strokeWidth={2.2} />
                  Edit info
                </button>
              )}
              {/* Admin-only (same gate as Edit) and only when there is
                  something to clear — the reset is a no-op otherwise. */}
              {EditModalComponent &&
                (typeof site.lat === 'number' ||
                  typeof site.long === 'number' ||
                  Boolean(site.site_image_url)) && (
                  <button
                    type="button"
                    onClick={() => setShowResetFieldData(true)}
                    className="border-line text-ink-2 hover:border-danger hover:text-danger hover:bg-danger-soft inline-flex items-center gap-1.5 rounded-[8px] border bg-white px-3.5 py-[7px] text-[12px] font-semibold transition-colors"
                  >
                    <MapPinOff size={11} strokeWidth={2.2} />
                    Reset location &amp; photo
                  </button>
                )}
            </div>
          </div>

          {/* Stat strip — 5 tiles divided */}
          <div className="border-line bg-line-2 grid grid-cols-2 border-t sm:grid-cols-3 lg:grid-cols-5">
            <HeroStat
              icon={Clock}
              value={s.surveys_scheduled}
              label="Surveys scheduled"
              cssVar="var(--color-brand)"
              iconBg="bg-brand-tint"
              iconColor="text-brand"
              numColor="text-brand"
              isFirst
            />
            <HeroStat
              icon={CheckCircle2}
              value={s.surveys_completed}
              label="Surveys completed"
              cssVar="var(--color-success)"
              iconBg="bg-success-soft"
              iconColor="text-success"
              numColor="text-success"
            />
            <HeroStat
              icon={CircleAlert}
              value={s.surveys_missed}
              label="Surveys missed"
              cssVar="var(--color-warning)"
              iconBg="bg-warning-soft"
              iconColor="text-warning"
              numColor="text-warning"
            />
            <HeroStat
              icon={AlertTriangle}
              value={issuesActiveCount}
              label="Issues active"
              cssVar="var(--color-danger)"
              iconBg="bg-danger-soft"
              iconColor="text-danger"
              numColor="text-danger"
            />
            <HeroStat
              icon={CheckCircle2}
              value={issuesResolvedCount}
              label="Issues resolved"
              cssVar="var(--color-success)"
              iconBg="bg-success-soft"
              iconColor="text-success"
              numColor="text-success"
            />
          </div>
        </section>
      </div>

      {/* ─────────── CONTENT PANE (scrolls; tabs bar stays pinned within it) ─────────── */}
      <section className="border-line flex min-h-0 flex-1 flex-col overflow-hidden rounded-[14px] border bg-white shadow-[0_2px_6px_-2px_rgba(11,21,48,0.04)]">
        {/* Tabs bar */}
        <div className="border-line flex shrink-0 items-center justify-between gap-3 border-b px-3 py-2">
          <div className="flex items-center gap-1">
            <TabButton
              isActive={activeTab === 'surveys'}
              onClick={() => setActiveTab('surveys')}
              icon={<CalendarCheck size={13} strokeWidth={2} />}
              label="Surveys"
              badge={surveysBadge}
            />
            <TabButton
              isActive={activeTab === 'issues'}
              onClick={() => setActiveTab('issues')}
              icon={<AlertTriangle size={13} strokeWidth={2} />}
              label="Issues"
              badge={issuesBadge}
            />
          </div>
        </div>

        {/* Tab content — scrolls internally so the hero card above stays static */}
        <div className="min-h-0 flex-1 overflow-y-auto">
          {activeTab === 'surveys' && <SiteSurveysTab siteId={siteId} />}
          {activeTab === 'issues' && <SiteIssuesTab siteId={siteId} />}
        </div>
      </section>

      {EditModalComponent && showEdit && (
        <EditModalComponent open onClose={() => setShowEdit(false)} site={site} />
      )}

      <ConfirmDialog
        open={showResetFieldData}
        onClose={() => setShowResetFieldData(false)}
        onConfirm={() =>
          resetFieldData(undefined, { onSuccess: () => setShowResetFieldData(false) })
        }
        variant="danger"
        title="Reset location & photo?"
        description={
          <>
            This clears <strong>both</strong> the site&apos;s GPS location and its
            building photo. The assigned field officer will have to capture them
            again on their next visit. The old photo is kept in storage but no
            longer shown.
          </>
        }
        confirmLabel="Yes, reset"
        isPending={isResettingFieldData}
      />
    </div>
  )
}

// ─── Tab button (pill style, matches site-detail mockup) ─────────────────

function TabButton({
  isActive,
  onClick,
  icon,
  label,
  badge,
}: {
  isActive: boolean
  onClick: () => void
  icon: React.ReactNode
  label: string
  badge: number
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        'inline-flex items-center gap-1.5 rounded-[8px] px-3.5 py-[7px] text-[13px] transition-colors',
        isActive
          ? 'bg-brand text-white font-semibold shadow-[0_6px_14px_-6px_rgba(11,59,145,0.45)]'
          : 'text-ink-3 hover:bg-line-2 hover:text-ink font-medium',
      )}
    >
      <span className="opacity-90">{icon}</span>
      {label}
      <span
        className={cn(
          'ml-0.5 inline-flex h-[18px] min-w-[18px] items-center justify-center rounded-full px-1.5 text-[10px] font-bold',
          isActive ? 'bg-white/22 text-white' : 'bg-line text-muted-text',
        )}
      >
        {badge}
      </span>
    </button>
  )
}
